import { CommonArea } from '@/shared/types';

/**
 * Shared validators for the residence and room admin editors.
 *
 * Each validator returns an error message string when the input is invalid, or
 * `null` when it passes. Callers decide how to surface the message (today via
 * `toast(...)`). Validators are intentionally framework-agnostic and side-effect
 * free so both editors and any future unit tests can reuse them.
 *
 * Two strictness levels per entity:
 *   - *Draft*  — minimal: just enough to identify the record in a list.
 *   - *Publish* — the full rules that gate public visibility (these reproduce the
 *     behaviour that used to live inline in the editors' handleSave functions).
 */

/** Returns true when `value` parses as a URL. */
const isValidUrl = (value: string): boolean => {
  try {
    new URL(value);
    return true;
  } catch {
    return false;
  }
};

// ---------------------------------------------------------------------------
// Residence
// ---------------------------------------------------------------------------

export interface ResidenceDraftInput {
  name?: string;
}

export interface ResidencePublishInput {
  name?: string;
  city?: string;
  address?: string;
  description?: string;
}

/** Draft residence: only a name, so it can be found again in the list. */
export const validateResidenceDraft = (residence: ResidenceDraftInput): string | null => {
  if (!residence.name || !residence.name.trim()) {
    return 'Please enter a name before saving a draft';
  }
  return null;
};

/**
 * Publishable residence: the core descriptive fields plus complete common areas.
 * (The "at least one publishable room" rule is enforced in the editor, which has
 * the room list and per-room status.)
 */
export const validateResidencePublish = (
  residence: ResidencePublishInput,
  commonAreas: CommonArea[] = [],
): string | null => {
  if (!residence.name || !residence.city || !residence.address || !residence.description) {
    return 'Please fill in all required fields';
  }

  for (const area of commonAreas) {
    const hasName = (area.name || '').trim() !== '';
    const hasImages = (area.imagesUrl || []).some((url) => url.trim() !== '');
    if (!hasName || !hasImages) {
      return 'Each common area needs a name and at least one photo. Complete it or remove it before publishing.';
    }
  }

  return null;
};

// ---------------------------------------------------------------------------
// Room (RoomType)
// ---------------------------------------------------------------------------

export interface RoomDraftInput {
  residenceId?: string;
  name?: string;
}

export interface RoomPublishInput {
  name?: string;
  description?: string;
  minStay?: number;
  priceByOccupancy?: number[];
  maxOccupancy?: number;
  area?: number;
  floorPlanUrl?: string;
  imagesUrl?: string[];
}

/**
 * Draft room: a residence to belong to and a name. `residenceId` is only
 * relevant for the standalone room editor; the residence modal sets it itself,
 * so pass it as already-known there.
 */
export const validateRoomDraft = (room: RoomDraftInput): string | null => {
  if (!room.residenceId) {
    return 'Please select a residence before saving a draft';
  }
  if (!room.name || !room.name.trim()) {
    return 'Please enter a room name before saving a draft';
  }
  return null;
};

/**
 * Publishable room: reproduces the full inline rules the editors used before —
 * required name/description/minStay, a valid price for every occupancy level,
 * a positive area, an optional-but-valid floor plan URL, and at least one valid
 * image URL.
 */
export const validateRoomPublish = (room: RoomPublishInput): string | null => {
  if (!room.name || !room.description || !room.minStay) {
    return 'Please fill in all required room fields';
  }

  const prices = room.priceByOccupancy || [];
  const expectedCount = room.maxOccupancy || 1;
  if (prices.length !== expectedCount || prices.some((p) => !p || p <= 0)) {
    return 'Please enter a valid price (> 0) for each occupancy level';
  }

  if (!room.area || room.area <= 0) {
    return 'Please enter a valid area (m²) greater than 0';
  }

  // Floor plan is optional, but if provided it must be a valid URL.
  if (room.floorPlanUrl && room.floorPlanUrl.trim() && !isValidUrl(room.floorPlanUrl)) {
    return 'Please enter a valid URL for the floor plan';
  }

  const filteredImages = (room.imagesUrl || []).filter((url) => url.trim() !== '');
  if (filteredImages.length === 0) {
    return 'Please add at least one room image URL';
  }
  if (filteredImages.some((url) => !isValidUrl(url))) {
    return 'Please enter valid URLs for all room images';
  }

  return null;
};
